/**
 * The page editing surface on web (plan 3.3, 7.3, 7.4): the page from
 * `editor-source` in an `<iframe sandbox="allow-scripts">` (no
 * allow-same-origin, so the page runs in an opaque origin) with the bridge
 * and this session's token (`withArtifactEditorSession`). Only messages
 * from that frame's window with the token and the bridge's shape are acted
 * on. Over the frame:
 *
 * - the element bar above the focused block: Move up, Move down,
 *   Duplicate, Remove; Edit link on links; Replace image, Edit with Tabbio
 *   and Alt text on images;
 * - the selection toolbar over selected text (Ask Tabbio and the quick
 *   actions);
 * - the findings banner, the length limit notice, and the suggestions strip
 *   with the mark beside the targeted block.
 *
 * Saves follow the op queue (800 ms, and at once on blur, Done, close and
 * when the tab is hidden); the panel header shows the save state.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@tabbio/tokens';
import { withArtifactEditorSession } from '@tabbio/utils/artifact-editor-bridge';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { SpinnerGlyph } from '../../common/SpinnerGlyph';
import { useArtifactVersion } from '../../../hooks/useArtifactVersions';
import { armArtifactAsk, pageSelection, sendArtifactQuickAction } from '../../../lib/artifact-ask';
import { runSelectionAction, type SelectionAction } from '../selection/selection-toolbar-model';
import { FloatingBar, SelectionToolbar } from '../selection/SelectionToolbar.web';
import { usePendingSuggestions } from '../suggestions/suggestions-store';
import { SuggestionStrip } from '../suggestions/SuggestionStrip.web';
import {
  AltTextDialog,
  EditLinkDialog,
  FindingsBanner,
  ReplaceImageDialog,
  type ReplaceImageChoice,
} from './EditorChrome.web';
import type { EditorSaveState } from './save-state';
import { usePageEditorController } from './usePageEditorController';

export type EditorHandle = {
  flush: () => Promise<boolean>;
  retry: () => void;
  reload: () => void;
};

type Dialog = { kind: 'link' | 'alt' | 'image'; id: string } | null;

function useStageSize(ref: React.RefObject<HTMLDivElement | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setSize({ width: node.clientWidth, height: node.clientHeight }));
    observer.observe(node);
    setSize({ width: node.clientWidth, height: node.clientHeight });
    return () => observer.disconnect();
  }, [ref]);
  return size;
}

/** Web: a hidden tab or a closed page sends what is queued. */
function watchBackground(flush: () => void): () => void {
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') flush();
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', flush);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', flush);
  };
}

export const PageEditor = forwardRef<
  EditorHandle,
  {
    artifactId: string;
    title: string;
    threadId: string | null;
    onSaveState: (state: EditorSaveState) => void;
    /** After Ask Tabbio or a quick action (a phone closes the panel so the chat shows). */
    onAsked?: () => void;
  }
>(function PageEditor({ artifactId, title, threadId, onSaveState, onAsked }, ref) {
  const { t } = useTranslation('common');
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const stage = useStageSize(stageRef);
  const [dialog, setDialog] = useState<Dialog>(null);

  const post = useCallback((message: unknown) => {
    frameRef.current?.contentWindow?.postMessage(message, '*');
  }, []);
  const controller = usePageEditorController({
    artifactId,
    post,
    expectedSource: () => frameRef.current?.contentWindow ?? null,
    accent: colors.primary,
    onBackground: watchBackground,
  });
  const { receive, snapshot, session, load } = controller;

  useEffect(() => {
    const onMessage = (event: MessageEvent) => receive(event.data, event.source);
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [receive]);

  useImperativeHandle(
    ref,
    () => ({ flush: controller.flush, retry: controller.retry, reload: controller.reload }),
    [controller.flush, controller.retry, controller.reload],
  );

  const onSaveStateRef = useRef(onSaveState);
  onSaveStateRef.current = onSaveState;
  useEffect(() => onSaveStateRef.current(controller.saveState), [controller.saveState]);

  // Leaving a block sends its change at once (the "blur" flush).
  const wasEditing = useRef(false);
  const { flush } = controller;
  useEffect(() => {
    const editing = Boolean(snapshot.element?.editing);
    if (wasEditing.current && !editing) void flush();
    wasEditing.current = editing;
  }, [flush, snapshot.element?.editing]);

  const srcDoc = useMemo(
    () => (load.status === 'ready' ? withArtifactEditorSession(load.html, controller.token) : null),
    [load, controller.token],
  );

  // The head's findings until the first save of the session answers its own.
  const headVersionId = load.status === 'ready' ? load.versionId : null;
  const head = useArtifactVersion(artifactId, headVersionId, { enabled: Boolean(headVersionId) });
  const findings = controller.checks?.findings ?? head.data?.findings ?? [];
  const warnings = controller.checks?.warnings ?? [];
  const errorCount = controller.checks ? controller.checks.findings.length : head.data?.checks.errors ?? 0;
  const warningCount = controller.checks ? controller.checks.warnings.length : head.data?.checks.warnings ?? 0;

  const base = controller.baseVersionId();
  const suggestions = usePendingSuggestions(artifactId, base);
  const [activeTarget, setActiveTarget] = useState<string | null>(null);

  const element = snapshot.element;
  const selection = snapshot.selection;
  const imageId = element?.role === 'image' ? element.id : snapshot.image?.id ?? null;
  const image = imageId && snapshot.image?.id === imageId ? snapshot.image : null;
  const link = snapshot.link;

  const backToPage = useCallback(() => {
    frameRef.current?.focus();
    if (element) session?.focus(element.id, true);
  }, [element, session]);

  const ask = async (action: SelectionAction) => {
    if (!selection) return;
    const captured = selection;
    await controller.flush();
    runSelectionAction(
      action,
      {
        artifactId,
        versionId: controller.baseVersionId(),
        threadId,
        selection: pageSelection(captured.id, captured.text),
        label: captured.text,
      },
      (key) => t(key),
    );
    onAsked?.();
  };

  const editImageWithTabbio = async (id: string, alt: string) => {
    await controller.flush();
    const label = t('chat.artifacts.selection.image', { title: alt || title });
    armArtifactAsk(
      { artifactId, versionId: controller.baseVersionId() ?? undefined, selection: pageSelection(id, alt || title) },
      label,
    );
    onAsked?.();
  };

  const fix = async () => {
    await controller.flush();
    sendArtifactQuickAction({ text: t('chat.artifacts.edit.fixPrompt'), context: { artifactId }, threadId });
    onAsked?.();
  };

  const chooseImage = (id: string, choice: ReplaceImageChoice) => {
    if (!session) return;
    if (choice.type === 'artifact') {
      session.replaceImage(id, { type: 'artifact', artifactId: choice.artifactId }, choice.displaySrc, choice.title || undefined);
    } else {
      session.replaceImage(id, { type: 'url', url: choice.url }, choice.url);
    }
    setDialog(null);
  };

  return (
    <div className="tabbio-page-editor">
      <FindingsBanner
        findings={findings}
        warnings={warnings}
        errorCount={errorCount}
        warningCount={warningCount}
        removed={controller.checks?.removed}
        onFix={() => void fix()}
      />
      <div ref={stageRef} className="tabbio-page-editor-stage">
        {load.status === 'loading' ? (
          <div className="tabbio-artifact-panel-message" role="status">
            <SpinnerGlyph color="var(--text-secondary)" size={22} />
            <span className="tabbio-artifact-panel-message-title">{t('chat.artifacts.edit.loading')}</span>
          </div>
        ) : load.status === 'error' ? (
          <div className="tabbio-artifact-panel-message">
            <span className="tabbio-artifact-panel-message-title">{t('chat.artifacts.edit.loadFailed')}</span>
            <button type="button" className="tabbio-panel-primary" onClick={controller.reload}>
              {t('chat.artifacts.edit.retry')}
            </button>
          </div>
        ) : srcDoc ? (
          <iframe
            key={load.generation}
            ref={frameRef}
            srcDoc={srcDoc}
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
            title={t('chat.artifacts.edit.frameTitle', { title })}
            className="tabbio-page-editor-frame"
          />
        ) : null}

        {snapshot.ready && !element && !selection ? (
          <div className="tabbio-page-editor-hint" aria-hidden>
            {t('chat.artifacts.edit.editHint')}
          </div>
        ) : null}

        {selection && stage.width > 0 ? (
          <SelectionToolbar
            rect={selection.rect}
            stage={stage}
            onAction={(action) => void ask(action)}
            onEscape={backToPage}
          />
        ) : element && stage.width > 0 ? (
          <FloatingBar
            rect={element.rect}
            stage={stage}
            label={t('chat.artifacts.toolbar.elementLabel')}
            onEscape={backToPage}
            className="is-element"
          >
            {link ? (
              <button type="button" className="tabbio-floating-button" onClick={() => setDialog({ kind: 'link', id: link.id })}>
                <MaterialSymbol name="link" size={16} />
                <span>{t('chat.artifacts.toolbar.editLink')}</span>
              </button>
            ) : null}
            {imageId ? (
              <>
                <button type="button" className="tabbio-floating-button" onClick={() => setDialog({ kind: 'image', id: imageId })}>
                  <MaterialSymbol name="image" size={16} />
                  <span>{t('chat.artifacts.toolbar.replaceImage')}</span>
                </button>
                <button
                  type="button"
                  className="tabbio-floating-button"
                  onClick={() => void editImageWithTabbio(imageId, image?.alt ?? element.alt ?? '')}
                >
                  <MaterialSymbol name="auto_awesome" size={16} />
                  <span>{t('chat.artifacts.toolbar.editImage')}</span>
                </button>
                <button type="button" className="tabbio-floating-button" onClick={() => setDialog({ kind: 'alt', id: imageId })}>
                  <MaterialSymbol name="text_fields" size={16} />
                  <span>{t('chat.artifacts.toolbar.altText')}</span>
                </button>
                <span className="tabbio-floating-divider" aria-hidden />
              </>
            ) : null}
            <button
              type="button"
              className="tabbio-floating-icon"
              disabled={!element.canMoveUp}
              onClick={() => session?.move(element.id, 'up')}
              aria-label={t('chat.artifacts.toolbar.moveUp')}
              title={t('chat.artifacts.toolbar.moveUp')}
            >
              <MaterialSymbol name="arrow_upward" size={18} />
            </button>
            <button
              type="button"
              className="tabbio-floating-icon"
              disabled={!element.canMoveDown}
              onClick={() => session?.move(element.id, 'down')}
              aria-label={t('chat.artifacts.toolbar.moveDown')}
              title={t('chat.artifacts.toolbar.moveDown')}
            >
              <MaterialSymbol name="arrow_downward" size={18} />
            </button>
            <button
              type="button"
              className="tabbio-floating-icon"
              onClick={() => session?.duplicate(element.id)}
              aria-label={t('chat.artifacts.toolbar.duplicate')}
              title={t('chat.artifacts.toolbar.duplicate')}
            >
              <MaterialSymbol name="content_copy" size={18} />
            </button>
            <button
              type="button"
              className="tabbio-floating-icon is-danger"
              onClick={() => session?.remove(element.id)}
              aria-label={t('chat.artifacts.toolbar.remove')}
              title={t('chat.artifacts.toolbar.remove')}
            >
              <MaterialSymbol name="delete" size={18} />
            </button>
          </FloatingBar>
        ) : null}

        {element && activeTarget === element.id && stage.width > 0 ? (
          <span
            className="tabbio-suggestion-mark"
            style={{ top: Math.max(4, element.rect.y - 10), insetInlineStart: Math.min(stage.width - 36, element.rect.x + element.rect.width - 12) }}
            aria-hidden
          >
            <MaterialSymbol name="lightbulb" size={14} />
          </span>
        ) : null}

        {snapshot.limit ? (
          <div className="tabbio-page-editor-limit" role="status">
            {t('chat.artifacts.edit.limit', { max: snapshot.limit.max })}
          </div>
        ) : null}
      </div>

      {suggestions.length ? (
        <SuggestionStrip
          items={suggestions}
          onReveal={(target) => {
            setActiveTarget(target);
            session?.focus(target);
          }}
        />
      ) : null}

      {dialog?.kind === 'link' && link ? (
        <EditLinkDialog
          href={link.href}
          text={link.text}
          onClose={() => setDialog(null)}
          onSave={(href, text) => {
            session?.editLink(dialog.id, href, text);
            setDialog(null);
          }}
        />
      ) : null}
      {dialog?.kind === 'alt' ? (
        <AltTextDialog
          alt={image?.alt ?? element?.alt ?? ''}
          onClose={() => setDialog(null)}
          onSave={(alt) => {
            session?.setAlt(dialog.id, alt);
            setDialog(null);
          }}
        />
      ) : null}
      {dialog?.kind === 'image' ? (
        <ReplaceImageDialog onClose={() => setDialog(null)} onChoose={(choice) => chooseImage(dialog.id, choice)} />
      ) : null}
    </div>
  );
});
